import { useMemo, useRef, useState, type FormEvent } from 'react';
import { ApiError, uploadPlanFiles } from '../api/client.js';
import type { PlanPublic } from '@plans/shared';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} بايت`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ك.ب`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} م.ب`;
}

type Props = {
  planId: string;
  onUploaded: (plan: PlanPublic) => void;
};

export function FolderUpload({ planId, onUploaded }: Props) {
  const filesRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [folderName, setFolderName] = useState<string>('');
  const [stage, setStage] = useState<'idle' | 'uploading' | 'validating' | 'done'>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const totalSize = useMemo(() => files.reduce((sum, file) => sum + file.size, 0), [files]);

  function acceptFiles(list: FileList | File[]) {
    const next = Array.from(list);
    setFiles(next);
    setSuccess(null);
    setError(null);
    setStage('idle');
    const first = next[0]?.webkitRelativePath.split('/')[0];
    setFolderName(first || next[0]?.name || 'المجلد المحدد');
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (files.length === 0) {
      setError('اختر ملفات أو مجلدًا أولًا.');
      return;
    }
    setError(null);
    setSuccess(null);
    setStage('uploading');
    setProgress(0);
    try {
      const paths = files.map((file) => file.webkitRelativePath || file.name);
      const result = await uploadPlanFiles(planId, files, paths, (percent) => {
        setProgress(percent);
        if (percent >= 100) {
          setStage('validating');
        }
      });
      setStage('done');
      setProgress(100);
      setSuccess('تم استبدال ملفات الخطة.');
      onUploaded(result.plan);
    } catch (err) {
      setStage('idle');
      setError(err instanceof ApiError ? err.message : 'تعذر رفع الملفات.');
    }
  }

  return (
    <form className="stack" onSubmit={(event) => void onSubmit(event)}>
      <p className="muted">
        ارفع ملفات أو مجلدًا. موقع HTML يبقى كما هو إذا كان فيه index.html. ملف واحد يُعرض كما هو
        (صورة أو PDF أو غيرهما). عدة ملفات تظهر كقائمة، وكل رابط يفتح الملف نفسه. الرفع الجديد
        يستبدل الملفات الحالية بعد اكتمال التحقق.
      </p>
      <div
        className="card dropzone"
        style={{ outline: dragOver ? '2px solid var(--shr-sky)' : undefined }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          if (event.dataTransfer.files.length > 0) {
            acceptFiles(event.dataTransfer.files);
          }
        }}
      >
        <div className="stack">
          <strong>ملفات الخطة</strong>
          <p className="muted">اختر ملفاتًا منفردة، أو مجلد موقع. تُحفظ المجلدات الداخلية كما هي.</p>
          <div className="row">
            <button type="button" className="btn secondary" onClick={() => filesRef.current?.click()}>
              اختيار ملفات
            </button>
            <button type="button" className="btn secondary" onClick={() => folderRef.current?.click()}>
              اختيار مجلد
            </button>
          </div>
          <input
            ref={filesRef}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              if (event.target.files) {
                acceptFiles(event.target.files);
              }
            }}
          />
          <input
            ref={(element) => {
              folderRef.current = element;
              if (element) {
                element.setAttribute('webkitdirectory', '');
                element.setAttribute('directory', '');
              }
            }}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              if (event.target.files) {
                acceptFiles(event.target.files);
              }
            }}
          />
          {files.length > 0 && (
            <p>
              <strong>{folderName}</strong>
              <br />
              <span className="muted">
                {files.length} ملفات · {formatBytes(totalSize)}
              </span>
            </p>
          )}
        </div>
      </div>
      {stage !== 'idle' && (
        <div>
          <div className="progress" aria-hidden="true">
            <span style={{ width: `${progress}%` }} />
          </div>
          <p className="muted">
            {stage === 'uploading' && `جارٍ الرفع… ${progress}%`}
            {stage === 'validating' && 'جارٍ التحقق والحفظ…'}
            {stage === 'done' && 'تم الحفظ'}
          </p>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}
      <div className="section-actions">
        <button className="btn" type="submit" disabled={files.length === 0 || stage === 'uploading'}>
          رفع واستبدال الملفات
        </button>
      </div>
    </form>
  );
}
